import { graphql } from 'gatsby';
import FooterColumn from './components/FooterColumn/FooterColumn';
import FooterLogo from './components/FooterLogo/FooterLogo';
import SocialLink from './components/FooterSocialLink/FooterSocialLink';
import { RichTextElement } from '@kontent-ai/gatsby-components';
import {
  StyledContainer,
  StyledCopyrightText,
  StyledFooter,
  StyledFootnotesGrid,
  StyledGrid,
  StyledGridContent,
  StyledIconSection,
  StyledListColumns,
  StyledListItemPrivacy,
  StyledPrivacyLinkList,
  StyledListSocialNetwork,
  StyledCertificationLogoList,
  StyledPrivacyAndCertificationGrid,
  StyledLink,
} from './Footer.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';
import FooterCertificationLogo from './components/FooterCertificationLogo/FooterCertificationLogo';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';

interface FooterProps {
  layout?: Queries.PAGE_LAYOUTFragment['page_layout__footer'];
}

export interface FooterLayoutProps extends Queries.FOOTERFragment, FooterProps {}

export const Footer = ({
  footerElements: elements,
  layout,
  id,
  system,
}: FooterLayoutProps & BlockHTMLId) => {
  const { privacy_links, columns, social_networks, copyright_text, logo, certification_logos } =
    elements;
  const hasCertificationLogos =
    !!certification_logos?.value && certification_logos.value.length > 0;

  return layout?.value === 'hidden' ? null : (
    <StyledFooter minified={layout?.value === 'minified'} id={id} data-codename={system.codename}>
      <StyledContainer>
        <StyledGrid>
          <FooterLogo {...logo.value} />
          <StyledGridContent>
            {layout?.value !== 'minified' && (
              <>
                <StyledListColumns>
                  {columns.value.map((column) => (
                    <FooterColumn key={column.id} {...column} />
                  ))}
                </StyledListColumns>
                <StyledIconSection>
                  <StyledListSocialNetwork>
                    {social_networks.value.map((socialLink) => (
                      <li key={socialLink.id}>
                        <SocialLink {...socialLink} />
                      </li>
                    ))}
                  </StyledListSocialNetwork>
                  {hasCertificationLogos && (
                    <StyledCertificationLogoList>
                      {certification_logos.value.map((certificationLogo) => (
                        <FooterCertificationLogo
                          key={certificationLogo.system.id}
                          {...certificationLogo}
                        />
                      ))}
                    </StyledCertificationLogoList>
                  )}
                  <LanguageSwitcher />
                </StyledIconSection>

                <StyledPrivacyAndCertificationGrid>
                  <StyledPrivacyLinkList>
                    {privacy_links.value.map((link) => (
                      <StyledListItemPrivacy key={link.system.id}>
                        <StyledLink {...link} />
                      </StyledListItemPrivacy>
                    ))}
                  </StyledPrivacyLinkList>
                </StyledPrivacyAndCertificationGrid>
              </>
            )}
            <StyledFootnotesGrid>
              <StyledCopyrightText>
                <RichTextElement value={copyright_text.value} />
              </StyledCopyrightText>
            </StyledFootnotesGrid>
          </StyledGridContent>
        </StyledGrid>
      </StyledContainer>
    </StyledFooter>
  );
};

export const fragment = graphql`
  fragment FOOTER on kontent_item_footer {
    system {
      codename
      type
      id
    }
    footerElements: elements {
      ...COMPONENT_A_B_TEST
      logo {
        value {
          ...FOOTER_LOGO
        }
      }
      columns {
        value {
          ...FOOTER_COLUMN
        }
      }
      copyright_text {
        value
      }
      social_networks {
        value {
          ...FOOTER_SOCIAL_LINK
        }
      }
      privacy_links {
        value {
          system {
            id
          }
          ...LINK
        }
      }
      certification_logos {
        value {
          ...FOOTER_CERTIFICATION_LOGO
        }
      }
    }
  }
`;

export default Footer;
